





































































































import {Component, Prop, Vue, Watch} from 'vue-property-decorator';
import {Inject} from "vue-typedi";
import { Datetime } from 'vue-datetime';
import { HistoryFilterRequest } from '@/model/history/HistoryFilterRequest';
import { OrderState } from '@/model/OrderState';
import { EventBus } from '@/helpers/EventBus';
import { StoreService } from '@/services/StoreService';
import { Zone } from '@/model/Zone';
import ZoneFilter, { ZoneFilterBehaviour } from '@/components/ZoneFilter.vue';
import dayjs from 'dayjs';
import {Drive} from "@/model/Drive";
import formatStoreLabel from '@/filters/StoreLabelFormatter';

@Component({
  components: {Datetime, ZoneFilter},
  filters: {formatStoreLabel}
})
export default class HistoryFilterPanel extends Vue {

    @Prop({default: {}}) filters: HistoryFilterRequest;
    @Prop({default: []}) private zones: Zone[];
    @Prop({default: []}) drives: Drive[];

    @Watch('filters', {immediate: true}) watchFilters() { this.updateCurrentFilters(this.filters); }

    @Inject()
    storeService!: StoreService;

    // Expose enums to the vue
    protected ZoneFilterBehaviour = ZoneFilterBehaviour;

    static readonly DATE_FORMAT = "YYYY-MM-DD";

    private startDate: string;
    private endDate: string;
    private status: string;
    protected maxStartDate: string;
    protected minEndDate: string;
    protected selectedZones: Zone[];
    public filteredDriveIds: string[] = [];

    // Use to reload the datepicker components when dates are updated
    private isUpdating: boolean = false;
    private isFilterChangeEventEnabled: boolean = false;

    mounted() {
        this.isFilterChangeEventEnabled = true;
        this.filteredDriveIds = [...this.drives.map(drive => drive.driveId)]

        EventBus.$on(EventBus.UPDATE_SELECTED_ZONES, () => {
            this.onFilterChange();
        });
    }

    @Watch('zones', {immediate: true}) 
    private loadSelectedZones(selectableZones: Zone[]) {
        const selectedZoneCodes = this.storeService.selectedZones.split(',');
        const selectedZones = selectableZones.filter(zone => selectedZoneCodes.includes(zone.zoneCode));
        if (selectedZones.length !== this.zones.length) {
            this.selectedZones = selectedZones;
            return;
        }

        this.selectedZones = [];
    }

    onZoneSelectionChanged(selectedZoneCodes: string[]) {
        this.selectedZones = this.zones.filter(zone => selectedZoneCodes.includes(zone.zoneCode));
        this.storeService.saveSelectedZones(selectedZoneCodes);
    }

    onFilterChange() {
        if (!this.isFilterChangeEventEnabled) {
            return;
        }

        this.isUpdating = true;


        const startDate = this.startDate ? dayjs(new Date(this.startDate)) : dayjs().startOf('month');
        const endDate = this.endDate ? dayjs(new Date(this.endDate)) : dayjs().endOf('month');

        this.updateLimitDate(startDate, endDate);
        
        EventBus.$emit(EventBus.HISTORY_FILTER_CHANGE, {
            startDate: startDate,
            endDate:  endDate,
            status: this.status === 'ALL' ? null : OrderState[this.status as keyof typeof OrderState],
            driveIds: this.filteredDriveIds
        });
        
        this.isUpdating = false;
    }

    private updateCurrentFilters(filters: HistoryFilterRequest) {
        if (!filters) {
            return;
        }
        
        const newStartDate = filters.startDate ? filters.startDate.format(HistoryFilterPanel.DATE_FORMAT) : dayjs().startOf('month').format(HistoryFilterPanel.DATE_FORMAT);
        if (newStartDate !== this.startDate) {
            this.startDate = newStartDate;
        }
        
        const newEndDate = filters.endDate ? filters.endDate.format(HistoryFilterPanel.DATE_FORMAT) : dayjs().format(HistoryFilterPanel.DATE_FORMAT);
        if (newEndDate !== this.endDate) {
            this.endDate = newEndDate;
        }
        
        const newStatus = filters.status ? OrderState[filters.status as OrderState] : 'ALL';
        if (newStatus !== this.status) {
            this.status = newStatus;
        }

        this.updateLimitDate(new Date(this.startDate), new Date(this.endDate));
    }

    private updateLimitDate(startDate: dayjs.ConfigType, endDate: dayjs.ConfigType) {
        this.maxStartDate = dayjs(endDate).format(HistoryFilterPanel.DATE_FORMAT);
        this.minEndDate = dayjs(startDate).format(HistoryFilterPanel.DATE_FORMAT);
    }

    private onClickYesterday() {
        this.startDate = dayjs().subtract(1, 'day').format(HistoryFilterPanel.DATE_FORMAT);
        this.endDate = this.startDate;
        this.onFilterChange();
    }

    private onClickLastWeek() {
        this.startDate = dayjs().subtract(1, 'week').startOf('week').format(HistoryFilterPanel.DATE_FORMAT);
        this.endDate = dayjs().subtract(1, 'week').endOf('week').format(HistoryFilterPanel.DATE_FORMAT);
        this.onFilterChange();
    }

    private onClickLastMonth() {
        this.startDate = dayjs().subtract(1, 'month').startOf('month').format(HistoryFilterPanel.DATE_FORMAT);
        this.endDate = dayjs().subtract(1, 'month').endOf('month').format(HistoryFilterPanel.DATE_FORMAT);
        this.onFilterChange();
    }

    get maxEndDate() {
        return dayjs().format(HistoryFilterPanel.DATE_FORMAT);
    }

}
